/* eslint-disable jsx-a11y/control-has-associated-label */
import { useTranslation } from 'next-i18next';
import { SingleValue } from 'react-select';
import { Dispatch, forwardRef, memo, SetStateAction, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import omit from 'lodash/omit';
import { BsXLg } from 'react-icons/bs';
import classNames from 'classnames';
import SearchDropdown from '../SearchDropdown';
import Button from '../Button';
import testIds from './testIds';
import analytics from '../../../core/analytics/analytics';
import GuestCounter from '../GuestCounter/GuestCounter';
import DatePicker from '../DatePicker/DatePicker';
import { CheckInAndOutDates, RoomAndGuestCount } from './useHotelSearchConditions';
import { getTotalChildCount } from '../GuestCounter/Counter';
import { COMMON_DATE_FORMAT } from '../../../core/constants';
import { PREFECTURES } from '../../../core/japaneseRegions';
import HotelSearchSelectOption from '../../../../types/HotelSearchSelectOption';
import convertCityPrefecture from '../../../core/helpers/convertCityPrefecture';
import SkipTranslationWrapper from '../SkipTranslationWrapper';
import SkipTranslationText from '../SkipTranslationText';

type Props = {
  id: string;
  searchTerm?: SingleValue<HotelSearchSelectOption>;
  size?: 'medium' | 'large';
  setSearchTerm: Dispatch<SetStateAction<SingleValue<HotelSearchSelectOption> | undefined>>;
  checkInAndOutDates: CheckInAndOutDates;
  setCheckInAndOutDates: Dispatch<SetStateAction<CheckInAndOutDates>>;
  roomAndGuestCount: RoomAndGuestCount;
  setRoomAndGuestCount: Dispatch<SetStateAction<RoomAndGuestCount>>;
  guestCountString: string;
  simple?: boolean;
  shallow?: boolean;
  isLoading?: boolean;
  roundedSearchBar?: boolean;
};

const HotelAvailabilitySearch = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      searchTerm,
      setSearchTerm,
      checkInAndOutDates,
      setCheckInAndOutDates,
      roomAndGuestCount,
      setRoomAndGuestCount,
      guestCountString,
      size = 'medium',
      simple = false,
      shallow = false,
      isLoading,
      roundedSearchBar,
    }: Props,
    ref
  ) => {
    const { t } = useTranslation('common');
    const [showFilter, setShowFilter] = useState(false);
    const router = useRouter();
    const isLocaleJA = router.locale === 'ja';
    const searchTermLabel = isLocaleJA ? searchTerm?.label.ja : searchTerm?.label.en;

    const events = {
      onClickSearch: () => {
        if (searchTerm && !isLoading) {
          const commonQuery = {
            checkInDate: checkInAndOutDates.checkInDate?.format(COMMON_DATE_FORMAT),
            checkOutDate: checkInAndOutDates.checkOutDate?.format(COMMON_DATE_FORMAT),
            roomCount: roomAndGuestCount.roomCount.toString(),
            guestPerRoom: guestCountString,
          };

          if (searchTerm.type === 'property') {
            router.push(
              {
                pathname: `/h/${searchTerm.value}`,
                query: {
                  ...commonQuery,
                },
              },
              undefined,
              {
                shallow,
              }
            );
          } else {
            const isCitySelected = searchTerm?.type === 'city';
            const isPrefectureSelected = PREFECTURES.find(
              (prefecture) => prefecture.path === searchTerm?.value
            );

            let type: string;
            if (isCitySelected) {
              type = 'city';
            } else if (isPrefectureSelected) {
              type = 'prefecture';
            } else {
              type = 'region';
            }

            const isPrefectureChanged = router.query.q !== searchTerm.value;
            const searchValue = isCitySelected
              ? convertCityPrefecture(searchTerm.label.en, searchTerm.prefecture?.en ?? '').join(
                  '_'
                )
              : searchTerm.value;

            router.push(
              {
                pathname: `/search/${type}/${searchValue}`,
                query: {
                  ...omit(router.query, ['type', 'q', 'tab', 'hotel', 'map']),
                  ...commonQuery,
                  prefectureAreas: isPrefectureChanged ? '' : router.query.prefectureAreas, // reset areas only when changing prefecture
                  latlng: isCitySelected ? searchTerm.value : '',
                },
              },
              undefined,
              {
                shallow,
              }
            );
          }

          analytics.google.basic.track.search(
            searchTerm.value,
            checkInAndOutDates?.checkInDate?.format(COMMON_DATE_FORMAT) as string,
            checkInAndOutDates?.checkOutDate?.format(COMMON_DATE_FORMAT) as string,
            roomAndGuestCount.roomCount,
            searchTerm.type,
            guestCountString
          );
        }
      },
    };

    const disabled = Boolean(
      !searchTerm?.value ||
        !checkInAndOutDates?.checkInDate ||
        !checkInAndOutDates?.checkOutDate ||
        !roomAndGuestCount?.roomCount ||
        !roomAndGuestCount?.adultCount
    );

    const renderSimpleSearchBar = () => {
      if (showFilter) {
        return (
          <>
            <div ref={ref} className="relative w-full">
              <div className="flex flex-col gap-2 rounded-md bg-white">
                <SearchDropdown
                  id={id}
                  dataTestId={testIds.searchTerm}
                  searchTerm={searchTerm}
                  onSearchTermChange={setSearchTerm}
                />
                <div className="h-12">
                  <DatePicker
                    checkInAndOutDates={checkInAndOutDates}
                    setCheckInAndOutDates={setCheckInAndOutDates}
                  />
                </div>
                <div className="h-12">
                  <GuestCounter
                    roomAndGuestCount={roomAndGuestCount}
                    setRoomAndGuestCount={setRoomAndGuestCount}
                  />
                </div>
                <div className="h-12">
                  <Button
                    className="size-full p-0 font-bold"
                    disabled={disabled}
                    onClick={events.onClickSearch}
                    size={size}
                    color="red"
                  >
                    {t('button.search_hotels_short')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-24 left-0 z-100 flex w-full justify-center bg-transparent">
              <Button
                className="!rounded-full p-4"
                onClick={() => setShowFilter(false)}
                size={size}
                color="white"
                textColor="black"
              >
                <BsXLg className="text-black" size={18} />
              </Button>
            </div>
            <div
              className="absolute inset-0 top-72 -z-50 h-screen w-screen bg-black opacity-60"
              onClick={() => setShowFilter(false)}
              onKeyDown={() => setShowFilter(false)}
              tabIndex={0}
              role="button"
            >
              <div />
            </div>
          </>
        );
      }

      return (
        <div
          ref={ref}
          className="flex w-full items-center justify-between rounded border border-gray-200 bg-white px-2 py-1 md:hidden"
          onClick={() => setShowFilter(true)}
          onKeyDown={() => setShowFilter(true)}
          role="button"
          tabIndex={0}
        >
          <div>
            <p className="font-bold">{searchTermLabel}</p>
            <span className="text-sm text-gray-600">
              <SkipTranslationWrapper tag="span">
                {checkInAndOutDates.checkInDate?.format('MMM Do')}
              </SkipTranslationWrapper>
              ・
              <SkipTranslationText
                i18nKey="hotel:availability.date_picker.stay_nights"
                values={{
                  stayNights: checkInAndOutDates?.checkOutDate?.diff(
                    checkInAndOutDates.checkInDate,
                    'days',
                    true
                  ),
                }}
              />
              ・
            </span>
            <span className="text-sm text-gray-600">
              <SkipTranslationText
                i18nKey="hotel:availability.guest_counter.total_room_and_guest_count"
                values={{
                  adultGuestCount: roomAndGuestCount.adultCount * roomAndGuestCount.roomCount,
                  childGuestCount:
                    getTotalChildCount(roomAndGuestCount) * roomAndGuestCount.roomCount,
                  roomCount: roomAndGuestCount.roomCount,
                }}
              />
            </span>
          </div>
          <div>
            <Image src="/magnifying_glass.svg" alt="tabist-magazine-logo" width={17} height={17} />
          </div>
        </div>
      );
    };

    return (
      <>
        <div
          ref={ref}
          className={classNames('flex-wrap grow justify-center items-center', {
            flex: !simple,
            'md:flex hidden': simple,
            'rounded-search-bar': roundedSearchBar,
            'gap-0': roundedSearchBar,
            'gap-2': !roundedSearchBar,
          })}
        >
          <div className="w-full max-w-full md:w-1/3 lg:w-1/4">
            <SearchDropdown
              id={id}
              dataTestId={testIds.searchTerm}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              roundedSearchBar={roundedSearchBar}
            />
          </div>
          <div
            className={classNames('grow w-full max-w-full h-12 md:w-1/3 lg:w-1/4 lg:h-14', {
              'h-14': roundedSearchBar,
            })}
          >
            <DatePicker
              checkInAndOutDates={checkInAndOutDates}
              setCheckInAndOutDates={setCheckInAndOutDates}
              roundedSearchBar={roundedSearchBar}
            />
          </div>
          <div
            className={classNames('grow w-full max-w-full h-12 md:w-3/4 lg:w-1/4 lg:h-14', {
              'md:w-1/3': roundedSearchBar,
            })}
          >
            <GuestCounter
              roomAndGuestCount={roomAndGuestCount}
              setRoomAndGuestCount={setRoomAndGuestCount}
              roundedSearchBar={roundedSearchBar}
            />
          </div>
          <div
            className={classNames('w-full max-w-full h-12 md:w-[15%] lg:w-[15%] lg:h-14', {
              'mt-2 md:mt-0': roundedSearchBar,
            })}
          >
            <Button
              className="size-full p-0 font-bold"
              disabled={disabled}
              loading={isLoading}
              onClick={events.onClickSearch}
              size={size}
              color="red"
              rounded={roundedSearchBar}
            >
              {t('button.search_hotels_short')}
            </Button>
          </div>
        </div>
        {simple && renderSimpleSearchBar()}
      </>
    );
  }
);

HotelAvailabilitySearch.displayName = 'HotelAvailabilitySearch';

export default memo(HotelAvailabilitySearch);
